/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/components/TimemarkForm.tsx
 * PURPOSE: Timemark clock-in form — last-resort attendance when GPS/Photo fails
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-09-16
 * COMPLIANCE: SDLC 6.5.0 + Zero-Mock Tolerance
 * PILLAR: P1 (Stage 04 - BUILD) + P2 (Sprint Governance)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * @business-context
 * - Domain: Attendance Timemark, Manual Clock-In, Manager Approval
 * - Impact: Last-resort clock-in — creates PENDING_APPROVAL record, 12h SLA
 * - Stakeholders: Employees (71), Branch Managers
 *
 * @technical-architecture
 * - Layer: Presentation Layer, UI Components
 * - Pattern: Form Component with Vietnamese UX
 * - Dependencies: React 18, attendanceApi (v2 timemark endpoint)
 *
 * @cross-references
 * - Design: docs/02-design/05-business-logic/ATTENDANCE-MULTI-METHOD-VERIFICATION-DESIGN.md §3.3
 * - Requirements: docs/01-planning/01-requirements/04-attendance/BR-ATTN-001-GPS-CLOCK-IN-OUT.md Scenario 6
 * - API: POST /api/v2/attendance/timemark-clock-in (backend/app/api/v2/endpoints/attendance_sprint71.py)
 * - Issue: docs/05-test/13-test-issues/bot-060.md (GH#871) — onSubmitted vs onSuccess contract
 */

import React, { useEffect, useState } from 'react';
import { Clock, Send, X, AlertTriangle } from 'lucide-react';
import { attendanceApi } from '../services/api/attendance';
import { useAuthStore } from '../store/auth';
import { CameraCapture } from '../features/attendanceModern/screens/CameraCapture';
import { locationService } from '../services/location';

interface TimemarkFormProps {
  onSuccess: () => void;
  onCancel: () => void;
  /**
   * bot-060 follow-up (GH#871, phát hiện 2026-09-16) — gọi NGAY khi API
   * submitTimemark() trả về thành công, KHÔNG đợi user tương tác với màn
   * hình success bên dưới. Trước đây caller chỉ có `onSuccess` (chạy khi
   * bấm nút "Về trang chủ") để báo toast, nên toast bị trễ theo hành động
   * đóng modal thay vì theo hành động API. Optional để không phá caller cũ
   * chưa cần notify tại thời điểm này.
   */
  onSubmitted?: () => void;
  /** Fallback chain recording failed verification attempts */
  fallbackChain?: Array<{ method: string; status: string; timestamp?: string }>;
}

export const TimemarkForm: React.FC<TimemarkFormProps> = ({
  onSuccess,
  onCancel,
  onSubmitted,
  fallbackChain,
}) => {
  const { accessToken } = useAuthStore();
  const [reason, setReason] = useState('');
  // SC-017-EMP-ATTENDANCE [6] amend (BS-004-R15, 2026-09-02) — ảnh tuỳ chọn,
  // không còn bắt buộc như bản mobile gốc. showCamera gates the in-app camera
  // UI open/closed (mirrors mobile's PhotoCapture 2-step: prompt → captured).
  const [photoDataUrl, setPhotoDataUrl] = useState<string | null>(null);
  const [showCamera, setShowCamera] = useState(false);
  // SC-017-EMP-ATTENDANCE [6] amend (BS-004-R15, 2026-09-02) — GPS TUỲ CHỌN:
  // lấy ngầm 1 lần lúc mở form nếu máy tự lấy được, KHÔNG chặn/KHÔNG hiện lỗi/
  // KHÔNG ép thử lại nếu không lấy được (khác hẳn GPS clock-in chính, nơi vị
  // trí là bắt buộc và có banner lỗi riêng).
  const [coords, setCoords] = useState<{ lat: number; lon: number } | null>(null);
  useEffect(() => {
    let cancelled = false;
    locationService.location_current_get__uses_gps__returns_coords()
      .then((c) => { if (!cancelled) setCoords({ lat: c.lat, lon: c.lon }); })
      .catch(() => { /* im lặng — Timemark không phụ thuộc GPS */ });
    return () => { cancelled = true; };
  }, []);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    approval_deadline: string;
    message_vi: string;
  } | null>(null);

  const handleSubmit = async () => {
    if (!accessToken) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await attendanceApi.submitTimemark(accessToken, {
        reason: reason.trim() || undefined,
        fallback_chain: fallbackChain,
        photo: photoDataUrl || undefined,
        latitude: coords?.lat,
        longitude: coords?.lon,
      });
      setResult({
        approval_deadline: response.approval_deadline,
        message_vi: response.message_vi,
      });
      // bot-060 follow-up: báo success NGAY tại thời điểm API thành công —
      // KHÔNG đợi user bấm "Về trang chủ" (onSuccess bên dưới vẫn giữ nguyên
      // ý nghĩa cũ: chỉ chạy khi user chủ động đóng/điều hướng).
      onSubmitted?.();
      // SC-017-EMP-ATTENDANCE [7]: đóng theo nút "Về trang chủ" tường minh
      // (registry), KHÔNG tự đóng sau vài giây — nhân viên cần đọc hạn phê duyệt.
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail
        : detail?.message ?? detail?.error ?? err?.message ?? '';
      setError(msg || 'Không thể gửi yêu cầu Timemark. Vui lòng thử lại.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // SC-017-EMP-ATTENDANCE [7] timemark_pending — success state.
  if (result) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6" data-testid="timemark-pending-screen">
        <div className="text-center">
          <div className="text-4xl mb-3">⏳</div>
          <h3 className="text-lg font-semibold text-green-700 mb-2">
            Đã gửi — chờ Quản lý duyệt
          </h3>
          <p className="text-sm text-gray-600 mb-2">
            Bạn sẽ nhận được thông báo khi yêu cầu được xử lý.
          </p>
          <p className="text-xs text-gray-500 mb-4">
            Hạn phê duyệt: {new Date(result.approval_deadline).toLocaleString('vi-VN')}
          </p>
          <button
            onClick={onSuccess}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
            data-testid="timemark-pending-go-home"
          >
            Về trang chủ
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Clock size={20} />
          Chấm công Timemark
        </h3>
        <button onClick={onCancel} className="p-1 hover:bg-gray-100 rounded">
          <X size={20} />
        </button>
      </div>

      {/* Warning */}
      <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
        <AlertTriangle size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-amber-800">
          <p className="font-medium">Cần phê duyệt quản lý</p>
          <p className="mt-1">
            Timemark là phương thức chấm công khi GPS và chụp ảnh không khả dụng.
            Yêu cầu sẽ được gửi đến Quản lý chi nhánh để phê duyệt trong vòng 12 giờ.
          </p>
        </div>
      </div>

      {/* Reason input */}
      <div className="mb-4">
        <label htmlFor="timemark-reason" className="block text-sm font-medium text-gray-700 mb-1">
          Lý do chấm công Timemark
        </label>
        <textarea
          id="timemark-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value.slice(0, 200))}
          placeholder="Ví dụ: GPS yếu trong bếp, camera bị che, thiết bị lỗi..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
          rows={3}
          maxLength={200}
        />
        <p className="text-xs text-gray-400 mt-1 text-right">{reason.length}/200</p>
      </div>

      {/* SC-017-EMP-ATTENDANCE [6] amend (BS-004-R15) — ảnh xác nhận TUỲ CHỌN. */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Ảnh xác nhận (không bắt buộc)</label>
        {photoDataUrl ? (
          <div>
            <img src={photoDataUrl} alt="Ảnh xác nhận Timemark" className="w-full rounded-lg mb-2" />
            <button
              onClick={() => setPhotoDataUrl(null)}
              className="text-xs text-gray-500 underline"
              type="button"
            >
              Xoá ảnh
            </button>
          </div>
        ) : showCamera ? (
          <CameraCapture
            required={false}
            testIdPrefix="timemark-camera"
            onCapture={(dataUrl) => {
              setPhotoDataUrl(dataUrl);
              setShowCamera(false);
            }}
            onCancel={() => setShowCamera(false)}
          />
        ) : (
          <button
            onClick={() => setShowCamera(true)}
            className="w-full px-3 py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
            type="button"
            data-testid="timemark-open-camera"
          >
            📷 Chụp ảnh (tuỳ chọn)
          </button>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg mb-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        <button
          onClick={onCancel}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
          disabled={isSubmitting}
        >
          Hủy
        </button>
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="flex-1 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 flex items-center justify-center gap-2 text-sm disabled:opacity-50"
        >
          {isSubmitting ? (
            <span>Đang gửi...</span>
          ) : (
            <>
              <Send size={16} />
              Chấm công cần duyệt
            </>
          )}
        </button>
      </div>
    </div>
  );
};
